import ReadingUtils from './ReadingUtils';

export interface EvaluationResult {
  refWord: string | null; // null for pure insertions
  spokenWord: string;
  index: number | null; // null for insertions
  correct: boolean;
  type: 'match' | 'insertion' | 'deletion' | 'substitution';
  isStutter?: boolean;
}

export function evaluateSpeakingAssessment(
  convertedSpeech: string,
  words: string[],
  wordIndex = 0,
): EvaluationResult[] {
  const spokenWords = convertedSpeech.toLowerCase().match(/\b[\w']+\b/g) || [];

  const referenceWords = words.slice(wordIndex).map((w) => w.toLowerCase());

  const results: EvaluationResult[] = [];
  let refPos = 0;
  let lastSpoken: string | null = null;
  let stutterCount = 0;
  const maxLookahead = 2;

  for (let i = 0; i < spokenWords.length; i++) {
    const sp = spokenWords[i];
    const isLast = i === spokenWords.length - 1;

    if (
      refPos < referenceWords.length &&
      ReadingUtils.soundsSimilar(sp, referenceWords[refPos])
    ) {
      results.push({
        refWord: referenceWords[refPos],
        spokenWord: sp,
        index: wordIndex + refPos,
        correct: true,
        type: 'match',
      });
      refPos++;
      lastSpoken = null;
      stutterCount = 0;
      continue;
    }

    let foundD = -1;
    for (
      let d = 1;
      d <= maxLookahead && refPos + d < referenceWords.length;
      d++
    ) {
      if (ReadingUtils.soundsSimilar(sp, referenceWords[refPos + d])) {
        foundD = d;
        break;
      }
    }

    if (foundD !== -1) {
      for (let j = 0; j < foundD; j++) {
        results.push({
          refWord: referenceWords[refPos],
          spokenWord: '',
          index: wordIndex + refPos,
          correct: false,
          type: 'deletion',
        });
        refPos++;
      }
      results.push({
        refWord: referenceWords[refPos],
        spokenWord: sp,
        index: wordIndex + refPos,
        correct: true,
        type: 'match',
      });
      refPos++;
      lastSpoken = null;
      stutterCount = 0;
      continue;
    }

    if (isLast && refPos < referenceWords.length) {
      results.push({
        refWord: referenceWords[refPos],
        spokenWord: sp,
        index: wordIndex + refPos,
        correct: false,
        type: 'substitution',
      });
      refPos++;
      lastSpoken = null;
      stutterCount = 0;
      continue;
    }

    if (sp === lastSpoken) {
      stutterCount++;
    } else {
      lastSpoken = sp;
      stutterCount = 1;
    }

    results.push({
      refWord: null,
      spokenWord: sp,
      index: null,
      correct: false,
      type: 'insertion',
      isStutter: stutterCount > 1,
    });
  }

  // Mark all remaining unspoken reference words as deletions
  while (refPos < referenceWords.length) {
    results.push({
      refWord: referenceWords[refPos],
      spokenWord: '',
      index: wordIndex + refPos,
      correct: false,
      type: 'deletion',
    });
    refPos++;
  }

  return results;
}
